// ═══ API Client — The Gathering V3 ═══

const BASE = '' // Vite proxy handles routing to localhost:5280

function getAuthHeaders(): Record<string, string> {
  const token = getToken()
  if (!token) return {}
  return { Authorization: `Bearer ${token}` }
}

function getToken(): string | null {
  return localStorage.getItem('session_token') || (document.cookie.match(/session=([^;]+)/)?.[1] ?? null)
}

export function decodeJWT(token: string) {
  try {
    return JSON.parse(atob(token.split('.')[1]))
  } catch {
    return null
  }
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const headers: Record<string, string> = {
    ...getAuthHeaders(),
    ...(options.headers as Record<string, string> || {}),
  }
  if (options.body && typeof options.body === 'string') {
    headers['Content-Type'] = 'application/json'
  }

  const res = await fetch(`${BASE}${path}`, { ...options, headers })

  if (res.status === 401) {
    // Token expired — clear and redirect
    localStorage.removeItem('session_token')
    document.cookie = 'session=; Max-Age=0; path=/'
    window.location.href = '/login'
    throw new Error('Unauthorized')
  }

  if (!res.ok) {
    const body = await res.text()
    throw new Error(body || `HTTP ${res.status}`)
  }

  const text = await res.text()
  if (!text) return {} as T
  return JSON.parse(text)
}

// ═══ Auth ═══

/** Who is looking at the page. Drives what the Incubator board lets you do. */
export function currentUserName(): string | null {
  const t = getToken()
  if (!t) return null
  const p = decodeJWT(t)
  return p?.name || p?.loginName || p?.userId || null
}

// ═══ Incubator pipeline ═══
// The board is Joel's inbox. Seconding and stage moves are gated server-side
// (agora-routes.js); these just call the doors.

export interface PipelineCard {
  id: string
  title: string
  author: string
  stage: string
  category: string | null
  seconds: number
  seconded_by: string[]
  replies: number
  brief: BriefSummary | null
  ready_for_brief: boolean
  brief_complete: boolean
  created_at: string
  last_moved: string | null
}

export type GateVerdict = 'pass' | 'fail' | 'unproven'
export const GATES = ['Demand', 'Timing', 'Fit', 'Edge', 'Autonomy'] as const

export interface Brief {
  what: string
  gates: Record<string, { verdict: GateVerdict; evidence: string }>
  ledger_sort: string | null
  build_shape: string
  kill_criteria: string
  author?: string
  updated_at?: string
}

export interface BriefSummary {
  passed: number
  failed: string[]
  unproven: string[]
  author: string | null
  updated_at: string | null
}

export async function fetchBrief(postId: string) {
  return request<{ brief: Brief; summary: BriefSummary }>(`/api/agora/posts/${encodeURIComponent(postId)}/brief`)
}

export async function saveBrief(postId: string, brief: Brief) {
  return request<{ ok: boolean; summary: BriefSummary }>(
    `/api/agora/posts/${encodeURIComponent(postId)}/brief`,
    { method: 'POST', body: JSON.stringify({ brief }) }
  )
}

export interface Pipeline {
  space: string
  columns: Record<string, PipelineCard[]>
  counts: Record<string, number>
  awaiting_joel: PipelineCard[]
  ready_for_brief: PipelineCard[]
  graveyard: PipelineCard[]
  parking_lot: PipelineCard[]
}

export async function fetchPipeline(slug = 'the-incubator') {
  return request<Pipeline>(`/api/agora/spaces/${encodeURIComponent(slug)}/pipeline`)
}

export async function secondSpark(postId: string, note?: string) {
  return request<{ ok: boolean; action: 'seconded' | 'unseconded'; seconds: number; seconded_by: string[] }>(
    `/api/agora/posts/${encodeURIComponent(postId)}/second`,
    { method: 'POST', body: JSON.stringify({ note: note || null }) }
  )
}

export async function moveStage(postId: string, to: string, note?: string) {
  return request<{ ok: boolean; from: string; to: string }>(
    `/api/agora/posts/${encodeURIComponent(postId)}/stage`,
    { method: 'POST', body: JSON.stringify({ to, note: note || null }) }
  )
}

export async function login(email: string, password: string, communitySlug?: string) {
  const body: Record<string, string> = { email, password }
  if (communitySlug) body.community_slug = communitySlug
  return request<import('./types').LoginResponse>('/api/auth/login', {
    method: 'POST',
    body: JSON.stringify(body),
  })
}

// ═══ Personas ═══

export async function fetchPersonas() {
  return request<import('./types').Persona[]>('/api/personas')
}

export async function fetchPersonaState() {
  return request<import('./types').PersonaStateResponse>('/api/personas/state')
}

export async function spawnPersona(name: string) {
  return request<{ ok: boolean }>(`/api/sessions/${name}/spawn`, { method: 'POST' })
}

export async function popoutPersona(name: string) {
  return request<{ ok: boolean }>(`/api/sessions/${name}/popout`, { method: 'POST' })
}

// ═══ Conversations ═══

export async function fetchConversations(limit = 50, community?: string) {
  const params = new URLSearchParams({ limit: String(limit) })
  if (community) params.set('community', community)
  const data = await request<import('./types').Conversation[] | { conversations: import('./types').Conversation[] }>(
    `/api/conversations?${params}`
  )
  // API returns a plain array, normalize to { conversations: [...] }
  const convs = Array.isArray(data) ? data : (data.conversations || [])
  for (const c of convs) {
    // Normalize participants — API may return plain strings or {persona: string} objects
    if (c.participants?.length && typeof c.participants[0] === 'string') {
      c.participants = (c.participants as unknown as string[]).map(p => ({ persona: p }))
    }
    // Normalize preview fields — API uses last_message/last_sender, types use last_message_preview/last_message_by
    const raw = c as unknown as Record<string, unknown>
    if (!c.last_message_preview && raw.last_message) c.last_message_preview = String(raw.last_message)
    if (!c.last_message_by && raw.last_sender) c.last_message_by = String(raw.last_sender)
  }
  return { conversations: convs }
}

export async function fetchConversation(id: string) {
  const data = await request<Record<string, unknown>>(`/api/conversations/${id}`)
  // Hetzner returns { conversation: {...}, participants: [...] } — unwrap and merge
  if (data.conversation && typeof data.conversation === 'object') {
    const conv = data.conversation as Record<string, unknown>
    if (data.participants) conv.participants = data.participants
    return conv as unknown as import('./types').Conversation
  }
  return data as unknown as import('./types').Conversation
}

export async function fetchMessages(convId: string) {
  const data = await request<import('./types').Message[] | { messages: import('./types').Message[] }>(
    `/api/conversations/${convId}/messages`
  )
  const msgs = Array.isArray(data) ? data : (data.messages || [])
  // Normalize: Hetzner PG returns sender_name/author_name, frontend expects sender
  for (const m of msgs) {
    const raw = m as unknown as Record<string, unknown>
    if (!m.sender && (raw.sender_name || raw.author_name)) {
      m.sender = String(raw.sender_name || raw.author_name)
    }
  }
  return { messages: msgs }
}

// findOrCreateConversation — if an existing 1:1/group with the exact participant set
// exists, return it; otherwise create one. Always returns a full Conversation object
// by doing a follow-up GET /api/conversations/{id} so callers can `setActiveConvo`
// directly without a second fetch.
export async function createConversation(participants: string[], title?: string, community_id?: string) {
  const createRes = await request<{ id: string; existed?: boolean }>('/api/conversations', {
    method: 'POST',
    body: JSON.stringify({ participants, title, community_id, find_or_create: true }),
  })
  if (!createRes?.id) return { conversation: null }
  try {
    const detail = await request<Record<string, unknown>>(`/api/conversations/${createRes.id}`)
    let conv: Record<string, unknown>
    if (detail.conversation && typeof detail.conversation === 'object') {
      conv = { ...(detail.conversation as Record<string, unknown>), participants: detail.participants || [] }
    } else {
      conv = detail
    }
    return { conversation: conv as unknown as import('./types').Conversation, existed: createRes.existed }
  } catch {
    return { conversation: { id: createRes.id, participants: [] } as unknown as import('./types').Conversation, existed: createRes.existed }
  }
}

// ═══ Projects ═══

export async function fetchProjects(status?: string) {
  const params = status ? `?status=${status}` : ''
  return request<import('./types').Project[]>(`/api/projects${params}`)
}

export async function fetchProject(id: string) {
  return request<import('./types').Project>(`/api/projects/${id}`)
}

export async function fetchProjectTasks(projectId: string) {
  return request<{ tasks: import('./types').ProjectTask[] }>(
    `/api/projects/${projectId}/tasks`
  )
}

// ═══ Activity ═══

export async function fetchActivityFeed(limit = 50, types?: string) {
  const params = new URLSearchParams({ limit: String(limit) })
  if (types) params.set('types', types)
  return request<{ events: import('./types').ActivityEvent[]; stats?: Array<{ event_type: string; count: number }> }>(
    `/api/activity/feed?${params}`
  )
}

// ═══ Community ═══

export async function fetchOperatorCommunities() {
  return request<{ communities: import('./types').Community[] }>('/api/operator/communities')
}
